import { EventListeners, MinzeElement, Reactive } from 'minze'
import axios from 'redaxios'
import { config } from '../../constants'
import { Locale } from '../../modules/locale'
import { Font } from '../../constants'

// This component takes data from the API /getOffersForCalendar and displays it in a calendar format.
// The data is formatted in the EventData interface.
// TODO: Cleanup EventData to match the actual data structure
export interface EventData {
  schema: string
  id: number
  name: {
    fr: string
    en: string
  }
  status: string
  urlConstruct: {
    fr: string
    en: string
  }
  image: {
    url: string
    alt: {
      [key: string]: string
    }
  }
  startDate: number
  endDate: number
  formattedDate: {
    fr: string
    en: string
  }
  location: {
    fr: string
    en: string
  }
  calendarSliderDate: {
    day: string
    month: string
    year: string
  }
  startDateRaw: number
  dayStartDate: number
  type: string
}

export interface LvcEventSeriesCalendar {
  events: EventData[]
  exhibitions: EventData[]
  eventSeries: EventData[]
  dataPageId: string
  dataPageType: string
  dataApiRoute: string
}

export class LvcEventSeriesCalendar extends MinzeElement {
  attrs = ['data-page-type', 'data-page-id']

  currentDateEvents: EventData[] = []
  currentDateEventsQuantity: number = 3
  reactive: Reactive = [['noEvents', false]]

  sortedEvents = (dateString: string) => {
    const date = new Date(dateString)
    const isSameDay = (d1: Date, d2: Date) => {
      return (
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
      )
    }
    return [...this.events].filter(event => {
      if (event.schema === 'exhibitionEvents' || event.schema === 'eventSeries') {
        const startDate = new Date(event.startDate * 1000)
        const endDate = new Date(event.endDate * 1000)
        return date >= startDate && date <= endDate
      } else {
        return isSameDay(
          new Date(
            event[event.schema === 'exhibitionEvents' ? 'dayStartDate' : 'startDate'] * 1000
          ),
          date
        )
      }
    })
  }

  onSelectDate = (event: Event) => {
    const detail = (event as CustomEvent).detail
    this.renderEvents(this.sortedEvents(detail?.selectedDate), 9999)
  }

  loadMoreEvents = () => {
    this.renderEvents(this.currentDateEvents, this.currentDateEventsQuantity + 3)
  }

  renderEvents = (events: EventData[], maxQuantity: number) => {
    this.currentDateEvents = events
    this.currentDateEventsQuantity = maxQuantity
    if (!this.shadowRoot) return
    const slots = this.shadowRoot.getElementById('slots')
    if (!slots) return

    const filteredEventOffers = this.currentDateEvents.filter(
      event => event.schema === 'eventOffers'
    )
    const filteredExhibitionEvents = this.currentDateEvents.filter(
      event => event.schema === 'exhibitionEvents'
    )
    const filteredEventSeries = this.currentDateEvents.filter(
      event => event.schema === 'eventSeries'
    )

    slots.innerHTML = `
      <lvc-event-series-list
        data-events="${encodeURIComponent(JSON.stringify(filteredEventOffers.slice(0, maxQuantity)))}"
        data-exhibitions="${encodeURIComponent(JSON.stringify(filteredExhibitionEvents.slice(0, maxQuantity)))}"
        data-event-series="${encodeURIComponent(JSON.stringify(filteredEventSeries.slice(0, maxQuantity)))}"
        data-collapsed="${events.length > events.slice(0, maxQuantity).length}"
      />
    `
  }

  dateToUnix(date: Date) {
    return Math.floor(date.getTime() / 1000)
  }

  async loadData(pageType: string, id: string) {
    try {
      const result = await axios.get(
        `${config.backendApi}/content/getOffersForCalendar?id=${id}&pageType=${pageType}`
      )
      const responseData = result?.data

      if (!responseData) {
        return (this.noEvents = true)
      }
      const { eventOffers, eventSeries, exhibitionEvents } = responseData
      const multiDayEventPopulated: any[] = []
      ;[...exhibitionEvents, ...eventSeries].forEach((item: any) => {
        const startDay = new Date(item.startDate * 1000) // TO MILLISECOND UNIX
        const endDay = new Date(item.endDate * 1000) // TO MILLISECOND UNIX
        for (
          let currentDay = startDay;
          currentDay <= endDay;
          currentDay.setDate(currentDay.getDate() + 1)
        ) {
          const dayStartUnix = this.dateToUnix(new Date(currentDay.getTime()))
          multiDayEventPopulated.push({
            ...item,
            dayStartDate: dayStartUnix,
          })
        }
      })

      const events = [...eventOffers, ...eventSeries, ...exhibitionEvents]
      this.events = events

      let dates = [...events]
      const uniqueDates = new Set<string>()
      const eventsWithUniqueDates: EventData[] = []

      ;[...eventOffers, ...multiDayEventPopulated].forEach((item, index) => {
        const now = new Date()
        now.setDate(now.getDate() - 1)
        let date = new Date(
          item[
            item.schema === 'exhibitionEvents' || item.schema === 'eventSeries'
              ? 'dayStartDate'
              : 'startDate'
          ] * 1000
        )
        if (date < now) return
        let calendarSliderDate = {
          day: date.getDate().toString(),
          month: Locale.getKey(`general.months.short.${date.getMonth() + 1}`),
          year: date.getFullYear().toString(),
        }
        item.calendarSliderDate = calendarSliderDate
        item.startDateRaw =
          item[
            item.schema === 'exhibitionEvents' || item.schema === 'eventSeries'
              ? 'dayStartDate'
              : 'startDate'
          ] * 1000

        const dateString = JSON.stringify(calendarSliderDate)

        if (!uniqueDates.has(dateString)) {
          uniqueDates.add(dateString)
          eventsWithUniqueDates.push(item)
        }
      })

      if (responseData.eventStart && responseData.eventEnd) {
        const originEventStartDate = new Date(responseData.eventStart).valueOf() / 1000 // TO SECONDS UNIX
        const originEventEndDate = new Date(responseData.eventEnd).valueOf() / 1000 // TO SECONDS UNIX

        // Filter out any dates that are outside of eventSeries date window
        dates = eventsWithUniqueDates.filter(
          item => item.endDate < originEventEndDate || item.startDate > originEventStartDate
        )
      } else {
        dates = eventsWithUniqueDates
      }

      const getTimeFromRaw = (rawDate: string) => new Date(rawDate).getTime()
      dates.sort((a, b) => getTimeFromRaw(a.startDateRaw) - getTimeFromRaw(b.startDateRaw))
      dates = dates.map((event: EventData) => ({
        ...event.calendarSliderDate,
        date: event.startDateRaw,
      }))
      if (!this.shadowRoot) return
      const slider = this.shadowRoot.getElementById('dates-container')
      if (!slider) return

      const datesString = encodeURIComponent(JSON.stringify(dates))
      slider.innerHTML = `<lvc-event-series-dates dates="${datesString}"></lvc-event-series-dates>`
      this.renderEvents(this.sortedEvents(dates[0].date), 9999)
    } catch (error) {
      return (this.noEvents = true)
    }
  }

  onReady() {
    const pageType: string = this.dataPageType
    const dataPageId: string = this.dataPageId
    if (!pageType || !dataPageId) return
    this.loadData(pageType, dataPageId)
  }

  html = () =>
    this.noEvents
      ? `<h2 class="no-events-title">${Locale.getKey('artistEvents.noEventsTitle')}</h2>`
      : `
    <div id="dates-container" class="mask w-slider-mask"></div>
    <div class="calendar">
      <div>
        <div id="slots"></div>
      </div>
    </div>
  `

  css = () => `
    :host {
      font-family: ${Font.family};
    }
  
    .calendar > div {
      padding: 40px 0 0 0;
    }
    
    .no-events-title {
      font-weight: ${Font.weight.medium};
      font-size: 40px;
      line-height: 120%;
      color: #1E007D;
    }
    
    @media screen and (max-width: 730px) {
      .calendar > div {
        padding: 16px 0 0 0;
      }
    }
  `

  eventListeners: EventListeners = [
    [window, 'lvc:event-series-date-change', this.onSelectDate],
    [window, 'lvc:event-series-load-more', this.loadMoreEvents],
  ]
}
